import PropTypes from 'prop-types';
import { AuthContext } from './auth-context';
import { authApi } from 'src/api/auth';
import { setSession } from './utils';
import { useEffect, useReducer, useCallback, useMemo } from 'react';
import { useSearchParams } from 'src/routes/hook';
import { useDomainContext } from 'src/context/domain-context';

const initialState = {
  user: null,
  company: null,
  loading: true,
  domainData: null,
  balance: null,
};

const reducer = (state, action) => {
  if (action.type === 'INITIAL') {
    return {
      loading: false,
      user: action.payload.user,
    };
  }
  if (action.type === 'LOGIN') {
    return {
      ...state,
      user: action.payload.account,
      company: action.payload.company,
    };
  }
  if (action.type === 'REGISTER') {
    return {
      ...state,
      user: action.payload.user,
    };
  }
  if (action.type === 'LOGOUT') {
    return {
      ...state,
      user: null,
      company: null,
    };
  }
  if (action.type === 'DOMAIN') {
    return {
      ...state,
      domainData: action.payload.domainData,
    };
  }
  if (action.type === 'BALANCE') {
    return {
      ...state,
      balance: action.payload.balance,
    };
  }
  return state;
};

// ----------------------------------------------------------------------

const STORAGE_KEY = 'accessToken';

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const searchParams = useSearchParams();
  const { domainInfo: domainData } = useDomainContext();

  const getCompanyInfo = useCallback(async () => {
    const setting = JSON.parse(domainData?.internal_brand?.theme_setting || '{}');
    let result = domainData;
    // Only persist a real id — setItem coerces undefined to the string
    // "undefined", which then leaks into form submissions as internal_brand_id.
    if (domainData?.internal_brand_id) {
      localStorage.setItem('brandId', domainData.internal_brand_id);
    }

    if (setting) {
      result = {
        ...result,
        colorPreset: setting?.color_preset,
        colorTheme: setting?.color_theme,
      };
    }

    dispatch({
      type: 'DOMAIN',
      payload: {
        domainData: result,
      },
    });
  }, [domainData]);

  const refreshUser = useCallback(async () => {
    try {
      const res = await authApi.getUser();
      const { client } = res.data;
      dispatch({
        type: 'INITIAL',
        payload: { user: client },
      }); 
    } catch (error) {
      console.error(error);
    }
  }, []);

  // LOGOUT
  const logout = useCallback(async () => {
    setSession(null);
    dispatch({
      type: 'LOGOUT',
    });
  }, []);

  const initialize = useCallback(async () => {
    try {
      const urlToken = searchParams.get('token');
      const accessToken = sessionStorage.getItem(STORAGE_KEY);
      if (accessToken && !urlToken) {
        setSession(accessToken);

        const res = await authApi.getUser();

        const { client } = res.data;

        dispatch({
          type: 'INITIAL',
          payload: {
            user: client,
          },
        });

        await getCompanyInfo();
      } else if (urlToken && domainData?.company_id) {
        logout();
        const loginRes = await authApi.getLoginToken({
          token: urlToken,
          company_id: domainData?.company_id,
        });

        const { token, account } = loginRes.data;

        setSession(token);
        localStorage.setItem("brandId", account.internal_brand_id);

        // The login-token endpoint returns a leaner `account` than the
        // canonical client account — it lacks fields like
        // `before_deposit_forms`. Fetch the full client so token-link logins
        // get the same user shape as password logins; otherwise deposit guards
        // (and anything else reading those fields) silently no-op until a manual
        // reload re-runs the getUser() path.
        const res = await authApi.getUser();
        const { client } = res.data;

        dispatch({
          type: 'INITIAL',
          payload: {
            user: client,
          },
        });

        await getCompanyInfo();
      } else {
        dispatch({
          type: 'INITIAL',
          payload: {
            user: null,
            company: null,
          },
        });
      }
    } catch (error) {
      console.error(error);
      dispatch({
        type: 'INITIAL',
        payload: {
          user: null,
          company: null,
        },
      });
    }
  }, [domainData?.company_id]);
  
  const updateBalance = (data) => {
    dispatch({
      type: 'BALANCE',
      payload: {
        balance: data,
      },
    });
  };

  useEffect(() => {
    initialize();
  }, [domainData?.company_id]);

  // LOGIN
  const login = useCallback(async (email, password, company_id) => {
    const data = {
      email,
      password,
      company_id,
    };

    const res = await authApi.login(data);
    const { company, token } = res.data;

    setSession(token);

    await getCompanyInfo();

    const resUser = await authApi.getUser();

    const { client } = resUser.data;

    dispatch({
      type: 'LOGIN',
      payload: {
        account: client,
        company,
      },
    });
  }, [domainData]);

  // ----------------------------------------------------------------------

  const checkAuthenticated = state.user ? 'authenticated' : 'unauthenticated';

  const status = state.loading ? 'loading' : checkAuthenticated;

  const memoizedValue = useMemo(
    () => ({
      user: state.user,
      account: state.account,
      company: state.company,
      domainData: state.domainData,
      balance: state.balance,
      method: 'jwt',
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
      //
      login,
      initialize,
      logout,
      getCompanyInfo,
      updateBalance,
      refreshUser,
    }),
    [
      initialize,
      login,
      logout,
      updateBalance,
      refreshUser,
      state.user,
      state.account,
      state.company,
      state.domainData,
      status,
    ]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}

AuthProvider.propTypes = {
  children: PropTypes.node,
};
